import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { TID } from "@/lib/testIds";
import Spotlight from "@/components/Spotlight";

const HOMEOWNER_OUTCOMES = [
  {
    k: "A warm welcome",
    d: "The first room of your home that feels designed to slow you down.",
  },
  {
    k: "A place to gather",
    d: "Big mornings, long dinners — your house starts to live in here.",
  },
  {
    k: "Quiet daily ease",
    d: "Storage that hides the mess. Light that flatters. Surfaces that forgive.",
  },
];

const BUILDER_STATS = [
  { v: 100, suffix: "%", label: "On-time delivery for 25+ years", decimals: 0 },
  { v: 100, suffix: "%", label: "Customer satisfaction", decimals: 0 },
  { v: 0, suffix: "%", label: "Post-install defect rate", decimals: 0 },
];

function Counter({ to, decimals = 0, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (to === 0) {
      setN(0);
      return;
    }
    const dur = 1600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <span ref={ref}>
      {n.toFixed(decimals)}
      {suffix}
    </span>
  );
}

function HomeownerColumn() {
  return (
    <motion.div
      data-testid={TID.value.column}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9 }}
      className="bg-sand text-charcoal p-10 md:p-14"
    >
      <p className="text-[11px] tracking-[0.32em] uppercase text-brassDark">
        For homeowners
      </p>
      <h3 className="mt-6 font-display font-light text-3xl md:text-4xl leading-tight max-w-md">
        A home that feels like an invitation.
      </h3>
      <p className="mt-6 text-ink leading-relaxed max-w-md">
        We don't measure homeowner work in percentages. The right kitchen is the
        one the people you love don't want to leave.
      </p>

      <ul className="mt-12 space-y-8">
        {HOMEOWNER_OUTCOMES.map((o) => (
          <li
            key={o.k}
            data-testid={TID.value.stat}
            className="border-t border-charcoal/15 pt-6"
          >
            <p className="font-display font-light text-3xl md:text-4xl leading-snug tracking-tight">
              {o.k}.
            </p>
            <p className="mt-3 text-ink leading-relaxed max-w-md">{o.d}</p>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function BuilderColumn() {
  return (
    <motion.div
      data-testid={TID.value.column}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9 }}
      className="bg-charcoal text-cream p-10 md:p-14"
    >
      <p className="text-[11px] tracking-[0.32em] uppercase text-brass">
        For builders & developers
      </p>
      <h3 className="mt-6 font-display font-light text-3xl md:text-4xl leading-tight max-w-md">
        A delivery you can program around.
      </h3>
      <div className="mt-12 space-y-10">
        {BUILDER_STATS.map((s) => (
          <div
            key={s.label}
            data-testid={TID.value.stat}
            className="border-t border-cream/15 pt-6"
          >
            <p className="font-display font-light text-[14vw] md:text-[5.4vw] leading-none tracking-tightest">
              <Counter to={s.v} decimals={s.decimals} suffix={s.suffix} />
            </p>
            <p className="mt-4 text-base text-cream/75">{s.label}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function Value() {
  return (
    <section
      id="value"
      data-testid={TID.value.root}
      className="relative bg-cream py-28 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <p className="text-[11px] tracking-[0.32em] uppercase text-ink mb-10">
          The Worth
        </p>
        <h2 className="font-display font-light text-[10vw] md:text-[5.4vw] leading-[0.98] tracking-tightest text-charcoal max-w-5xl">
          Designed to deliver more.
        </h2>

        <div className="mt-16 md:mt-24 grid md:grid-cols-2 gap-4 md:gap-6">
          <Spotlight tone="light" radius={460}>
            <HomeownerColumn />
          </Spotlight>
          <Spotlight tone="dark" radius={460}>
            <BuilderColumn />
          </Spotlight>
        </div>
      </div>
    </section>
  );
}
